import type { QueryKey } from '@tanstack/react-query';
import type { PaginationType } from '../types/Pagination.types';
import type { GameDto, GameLaunchUrlPayload, GameListItemDto } from './games.schema';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { QueriesKeys } from '../queriesKeys';
import { gamesApi } from './games.api';

export const useCreateShareLink = () => {
  return useMutation({
    mutationFn: ({ gameId, roundId }: { gameId: string; roundId: string }) =>
      gamesApi.createShareLink(gameId, roundId),
  });
};

export const useVerifyRound = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (roundId: string) => gamesApi.verifyRound(roundId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueriesKeys.roundsWithWins] });
    },
  });
};

export const useGameLaunchUrl = () => {
  return useMutation({
    mutationFn: (payload: GameLaunchUrlPayload) => gamesApi.getLaunchUrl(payload),
  });
};

type DislikeGameContext = {
  previousFavoritesQueries: Array<[QueryKey, PaginationType<GameListItemDto> | undefined]>;
};

export const useDislikeGame = () => {
  const queryClient = useQueryClient();

  return useMutation<GameDto, unknown, string, DislikeGameContext>({
    mutationFn: (gameId: string) => gamesApi.dislikeGame(gameId),
    async onMutate(gameId) {
      await queryClient.cancelQueries({ queryKey: [QueriesKeys.games, 'favorites'] });

      const previousFavoritesQueries = queryClient.getQueriesData<PaginationType<GameListItemDto>>({
        queryKey: [QueriesKeys.games, 'favorites'],
      });

      previousFavoritesQueries.forEach(([queryKey, oldData]) => {
        if (!oldData) {
          return;
        }
        queryClient.setQueryData<PaginationType<GameListItemDto>>(queryKey, {
          ...oldData,
          items: oldData.items.filter(game => game.id !== gameId),
          totalItems: Math.max(0, oldData.totalItems - 1),
        });
      });

      return { previousFavoritesQueries };
    },
    onError: (_error, _gameId, context) => {
      context?.previousFavoritesQueries?.forEach(([queryKey, previousData]) => {
        queryClient.setQueryData(queryKey, previousData);
      });
    },
  });
};
